import {
  ClientsApi,
  Configuration,
  ServiceProvidersApi,
  StateSnapshotsApi,
  StudentsApi,
  UsersApi,
} from "../profile-sdk";
import { SessionsApi } from "../session-sdk";
import {
  AccountStudentsApi,
  ClientSessionCountsApi,
  DuplicateStudentReportsApi,
  FinancialDashboardReportApi,
  HTTPHeaders,
  Configuration as ReportingConfig,
  SessionCountApi,
  SessionLogsApi,
  StudentProgressReportApi,
  StudentInstancesReportApi,
} from "@xng/reporting";
import {
  NotificationsApi,
  Configuration as XngBackendConfig,
  StateSnapshotsApi as StateSnapshotsNPMApi,
  StudentsApi as XngBackendStudentsApi,
  ServiceProvidersApi as XngBackendServiceProvidersApi,
  SessionsApi as XngBackendSessionsApi,
  UsersApi as XngBackendUsersApi,
  DistrictsApi as XngBackendDistrictsApi,
  CampusesApi as XngBackendCampusesApi,
  ClientsApi as XngBackendClientsApi,
  StatesApi as XngBackendStatesApi,
  XLogsApi as XngBackendXLogsApi,
}  from "@xng/xng-backend";
import {
  ReportingApi as V2ReportingApi,
  Configuration as V2ReportingConfig,
} from "@xng/xng-reporting";
import {
  BillingCyclesApi as BillingBillingCyclesApi,
  BundleRunsApi as BillingBundleRunsApi,
  ClaimAckReviewsApi as BillingClaimAckReviewsApi,
  ClaimsApi as BillingClaimsApi,
  ClientsApi as BillingClientsApi,
  Configuration as BillingConfig,
  CorrectionWarningsApi as BillingCorrectionWarningsApi,
  DenialsApi as BillingDenialsApi,
  EdiFilesApi as BillingEdiFilesApi,
  LineResubmissionsApi as BillingLineResubmissionsApi,
  LossesApi as BillingLossesApi,
  RemittanceReviewsApi as BillingRemittanceReviewsApi,
  RemittancesApi as BillingRemittancesApi,
} from "@xng/xng-billing";
import {
  ClientsApi as AdminClientsApi,
  Configuration as AdminConfig,
  DistrictsApi as AdminDistrictsApi,
  XLogsApi as AdminXLogsApi,
} from "@xng/xng-admin";
import { APIM_SUBSCRIPTION_KEY } from "../constants/apimSubscriptionKey";
import { eventEmitter } from "../event_emitter";
import { APIConfigManager } from "./Api_Config";

/**
 * This file contains the API initialization logic.
 * It is called via msalEventCallback.ts when a user logs in or acquires a token.
 */

// Header used to authenticate with the Reporting API
// APIM_SUBSCRIPTION_KEY comes from the developers local environment
const reportingHeaders: HTTPHeaders = {};
if (APIM_SUBSCRIPTION_KEY) {
  reportingHeaders["Ocp-Apim-Subscription-Key"] = APIM_SUBSCRIPTION_KEY;
}
// This is a flag to check if the APIs have been initialized
let apiReady = false;
let _bearerToken: string | undefined;

/**
 * Returns the current bearer token used by the APIs.
 * Useful for authenticated downloads that bypass the SDK.
 */
export function getApiBearerToken(): string | undefined {
  return _bearerToken;
}

/**
 * Initializes the APIs with the bearer token.
 *
 * @param token - The bearer token used to authenticate the APIs.
 */
export function initializeAPIs(token: any) {
  _bearerToken = token;
  // This is the API configuration manager instance that is used to get the API URLs
  const apiConfigManager = APIConfigManager.getInstance();

  // Initialize the API configurations with the bearer token

  const PROFILE_CONFIG = new Configuration({
    apiKey: `Bearer ${token}`,
    basePath: apiConfigManager.config.PROFILE_API_URL,
  });

  const SESSIONS_CONFIG = new Configuration({
    apiKey: `Bearer ${token}`,
    basePath: apiConfigManager.config.SESSION_API_URL,
  });

  // The Reporting API configuration additionally includes the headers from the reportingHeaders object
  const REPORTING_CONFIG = new ReportingConfig({
    accessToken: token,
    basePath: apiConfigManager.config.REPORTING_API_URL,
    headers: reportingHeaders,
  });

  const BACKEND_CONFIG = new XngBackendConfig({
    basePath: apiConfigManager.config.BACKEND_API_URL,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  // V2 Reporting API configuration (new Aspire-hosted backend)
  const v2ReportingUrl = apiConfigManager.config.V2_REPORTING_API_URL;
  if (v2ReportingUrl) {
    const V2_REPORTING_CONFIG = new V2ReportingConfig({
      basePath: v2ReportingUrl,
      accessToken: token,
    });
    API_REPORTING_V2 = new V2ReportingApi(V2_REPORTING_CONFIG);
  }

  // Billing API (XNG.Billing) — configured only where BILLING_API_URL exists
  // (local/dbr/UAT today); call sites must guard for undefined.
  const billingUrl = apiConfigManager.config.BILLING_API_URL;
  if (billingUrl) {
    const BILLING_CONFIG = new BillingConfig({
      basePath: billingUrl,
      accessToken: token,
    });
    API_BILLING_CLAIMS = new BillingClaimsApi(BILLING_CONFIG);
    API_BILLING_BUNDLE_RUNS = new BillingBundleRunsApi(BILLING_CONFIG);
    API_BILLING_REMITTANCES = new BillingRemittancesApi(BILLING_CONFIG);
    API_BILLING_REMITTANCE_REVIEWS = new BillingRemittanceReviewsApi(BILLING_CONFIG);
    API_BILLING_CLAIM_ACK_REVIEWS = new BillingClaimAckReviewsApi(BILLING_CONFIG);
    API_BILLING_CORRECTION_WARNINGS = new BillingCorrectionWarningsApi(BILLING_CONFIG);
    API_BILLING_LINE_RESUBMISSIONS = new BillingLineResubmissionsApi(BILLING_CONFIG);
    API_BILLING_DENIALS = new BillingDenialsApi(BILLING_CONFIG);
    API_BILLING_LOSSES = new BillingLossesApi(BILLING_CONFIG);
    API_BILLING_CLIENTS = new BillingClientsApi(BILLING_CONFIG);
    API_BILLING_EDI_FILES = new BillingEdiFilesApi(BILLING_CONFIG);
    API_BILLING_CYCLES = new BillingBillingCyclesApi(BILLING_CONFIG);
  }

  // MSB admin API (XLogs.Backend.Admin) — configured only where ADMIN_API_URL
  // exists; call sites must guard for undefined.
  const adminUrl = apiConfigManager.config.ADMIN_API_URL;
  if (adminUrl) {
    const ADMIN_CONFIG = new AdminConfig({
      basePath: adminUrl,
      accessToken: token,
    });
    API_ADMIN_DISTRICTS = new AdminDistrictsApi(ADMIN_CONFIG);
    API_ADMIN_XLOGS = new AdminXLogsApi(ADMIN_CONFIG);
    API_ADMIN_CLIENTS = new AdminClientsApi(ADMIN_CONFIG);
  }

  // Initialize the APIs with the configurations
  API_SESSION_LOGS = new SessionLogsApi(REPORTING_CONFIG);
  API_DUPLICATE_STUDENT_REPORTS = new DuplicateStudentReportsApi(REPORTING_CONFIG);
  API_ACCOUNT_STUDENTS = new AccountStudentsApi(REPORTING_CONFIG);
  API_SESSIONS_COUNT = new SessionCountApi(REPORTING_CONFIG);
  API_CLIENT_SESSION_COUNTS = new ClientSessionCountsApi(REPORTING_CONFIG);
  API_FINANCIAL_DASHBOARD_REPORT = new FinancialDashboardReportApi(REPORTING_CONFIG);
  API_STUDENTS = new StudentsApi(PROFILE_CONFIG);
  API_STUDENTS_BACKEND = new XngBackendStudentsApi(BACKEND_CONFIG);
  API_DISTRICTS_BACKEND = new XngBackendDistrictsApi(BACKEND_CONFIG);
  API_USERS = new UsersApi(PROFILE_CONFIG);
  API_USERS_BACKEND = new XngBackendUsersApi(BACKEND_CONFIG);
  API_XLOGS_BACKEND = new XngBackendXLogsApi(BACKEND_CONFIG);
  API_CLIENTS = new ClientsApi(PROFILE_CONFIG);
  API_CLIENTS_BACKEND = new XngBackendClientsApi(BACKEND_CONFIG);
  API_STATES_BACKEND = new XngBackendStatesApi(BACKEND_CONFIG);
  API_STATESNAPSHOTS = new StateSnapshotsApi(PROFILE_CONFIG);
  API_STATESNAPSHOTSNPM = new StateSnapshotsNPMApi(BACKEND_CONFIG);
  API_SERVICEPROVIDERS = new ServiceProvidersApi(PROFILE_CONFIG);
  API_SERVICEPROVIDERS_BACKEND = new XngBackendServiceProvidersApi(BACKEND_CONFIG);
  API_SESSIONS = new SessionsApi(SESSIONS_CONFIG);
  API_STUDENT_PROGRESS_REPORT = new StudentProgressReportApi(REPORTING_CONFIG);
  API_STUDENT_INSTANCES_REPORT = new StudentInstancesReportApi(REPORTING_CONFIG);
  API_NOTIFICATIONS = new NotificationsApi(BACKEND_CONFIG);
  API_SESSIONS_BACKEND = new XngBackendSessionsApi(BACKEND_CONFIG);
  API_CAMPUSES_BACKEND = new XngBackendCampusesApi(BACKEND_CONFIG);
  // If the APIs have not been initialized, dispatch an event to let the app know that the APIs are ready
  // This is used in the AuthProvider component
  if (!apiReady) {
    const event = new CustomEvent("apiInitialized");
    eventEmitter.dispatchEvent(event);
  }
  // Set the flag to true to indicate that the APIs have been initialized, preventing them from being unnecessarily initialized again
  apiReady = true;
}

// Export the APIs
export let API_SESSION_LOGS: SessionLogsApi;
export let API_DUPLICATE_STUDENT_REPORTS: DuplicateStudentReportsApi;
export let API_ACCOUNT_STUDENTS: AccountStudentsApi;
export let API_SESSIONS_COUNT: SessionCountApi;
export let API_STUDENTS: StudentsApi;
export let API_STUDENTS_BACKEND: XngBackendStudentsApi; // This is the Students API from the xng-backend package, which is used for the student profile page
export let API_DISTRICTS_BACKEND: XngBackendDistrictsApi; // This is the Districts API from the xng-backend package, which is used for district related operations
export let API_USERS: UsersApi;
export let API_USERS_BACKEND: XngBackendUsersApi; // This is the Users API from the xng-backend package, which is used for the staff directory page
export let API_XLOGS_BACKEND: XngBackendXLogsApi; // Resolved runtime configuration (feature flags) for the logged-in account
export let API_CLIENTS: ClientsApi;
export let API_CLIENTS_BACKEND: XngBackendClientsApi;
export let API_STATES_BACKEND: XngBackendStatesApi;
export let API_STATESNAPSHOTS: StateSnapshotsApi;
export let API_STATESNAPSHOTSNPM: StateSnapshotsNPMApi;
export let API_SERVICEPROVIDERS: ServiceProvidersApi;
export let API_SERVICEPROVIDERS_BACKEND: XngBackendServiceProvidersApi; // This is the Service Providers API from the xng-backend package, which is used for the staff directory page
export let API_SESSIONS: SessionsApi;
export let API_CLIENT_SESSION_COUNTS: ClientSessionCountsApi;
export let API_FINANCIAL_DASHBOARD_REPORT: FinancialDashboardReportApi;
export const REPORTING_HEADERS = reportingHeaders;
export let API_STUDENT_PROGRESS_REPORT:StudentProgressReportApi
export let API_STUDENT_INSTANCES_REPORT:StudentInstancesReportApi;
export let API_NOTIFICATIONS: NotificationsApi;
export let API_SESSIONS_BACKEND: XngBackendSessionsApi;
export let API_CAMPUSES_BACKEND: XngBackendCampusesApi;
export let API_REPORTING_V2: V2ReportingApi | undefined;
export let API_BILLING_CLAIMS: BillingClaimsApi | undefined;
export let API_BILLING_BUNDLE_RUNS: BillingBundleRunsApi | undefined;
export let API_BILLING_REMITTANCES: BillingRemittancesApi | undefined;
export let API_BILLING_REMITTANCE_REVIEWS: BillingRemittanceReviewsApi | undefined;
export let API_BILLING_CLAIM_ACK_REVIEWS: BillingClaimAckReviewsApi | undefined;
export let API_BILLING_CORRECTION_WARNINGS: BillingCorrectionWarningsApi | undefined;
export let API_BILLING_LINE_RESUBMISSIONS: BillingLineResubmissionsApi | undefined;
export let API_BILLING_DENIALS: BillingDenialsApi | undefined;
export let API_BILLING_LOSSES: BillingLossesApi | undefined;
export let API_BILLING_CLIENTS: BillingClientsApi | undefined;
export let API_BILLING_EDI_FILES: BillingEdiFilesApi | undefined;
export let API_BILLING_CYCLES: BillingBillingCyclesApi | undefined;
export let API_ADMIN_DISTRICTS: AdminDistrictsApi | undefined;
export let API_ADMIN_XLOGS: AdminXLogsApi | undefined;
export let API_ADMIN_CLIENTS: AdminClientsApi | undefined;
